import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';
import { ApiErrorHandler } from '../error.handler';
import {
  AvailablePhoneNumber,
  AvailablePhoneNumbersApiResponse,
  PhoneNumber,
  PhoneNumberApiResponse,
  PhoneNumbersApiResponse,
  PurchasePhoneNumberPayload,
  SearchPhoneNumbersParams,
  UpdatePhoneNumberPayload,
} from '../../../types/phone-numbers';

class PhoneNumbersEndpoint extends BaseEndpoint {
  private static instance: PhoneNumbersEndpoint;

  private constructor() {
    super('organisations');
  }

  static getInstance(): PhoneNumbersEndpoint {
    if (!PhoneNumbersEndpoint.instance) {
      PhoneNumbersEndpoint.instance = new PhoneNumbersEndpoint();
    }
    return PhoneNumbersEndpoint.instance;
  }

  async searchAvailable(
    organisationId: string,
    params: SearchPhoneNumbersParams = {}
  ): Promise<AvailablePhoneNumber[]> {
    try {
      const headers = await this.getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (params.country) queryParams.set('country', params.country);
      if (params.areaCode) queryParams.set('areaCode', params.areaCode);
      if (params.contains) queryParams.set('contains', params.contains);
      if (params.numberType) queryParams.set('numberType', params.numberType);
      if (params.limit !== undefined) queryParams.set('limit', String(params.limit));

      const response = await httpClient.get<AvailablePhoneNumbersApiResponse>(
        `${this.basePath}/${organisationId}/twilio/phone-numbers/search?${queryParams.toString()}`,
        { headers }
      );

      logger.info('Available phone numbers fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to search available phone numbers:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async list(organisationId: string): Promise<PhoneNumber[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<PhoneNumbersApiResponse>(
        `${this.basePath}/${organisationId}/twilio/phone-numbers`,
        { headers }
      );

      logger.info('Phone numbers fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch phone numbers:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getById(organisationId: string, phoneNumberId: number): Promise<PhoneNumber> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<PhoneNumberApiResponse>(
        `${this.basePath}/${organisationId}/twilio/phone-numbers/${phoneNumberId}`,
        { headers }
      );

      logger.info('Phone number fetched successfully', { phoneNumberId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch phone number:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async purchase(
    organisationId: string,
    payload: PurchasePhoneNumberPayload
  ): Promise<PhoneNumber> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<PhoneNumberApiResponse>(
        `${this.basePath}/${organisationId}/twilio/phone-numbers`,
        payload,
        { headers }
      );

      logger.info('Phone number purchased successfully', { phoneNumber: payload.phoneNumber });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to purchase phone number:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async update(
    organisationId: string,
    phoneNumberId: number,
    payload: UpdatePhoneNumberPayload
  ): Promise<PhoneNumber> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<PhoneNumberApiResponse>(
        `${this.basePath}/${organisationId}/twilio/phone-numbers/${phoneNumberId}`,
        payload,
        { headers }
      );

      logger.info('Phone number updated successfully', { phoneNumberId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update phone number:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async release(organisationId: string, phoneNumberId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/${organisationId}/twilio/phone-numbers/${phoneNumberId}`,
        { headers }
      );

      logger.info('Phone number released successfully', { phoneNumberId });
    } catch (error) {
      logger.error('Failed to release phone number:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const phoneNumbersEndpoint = PhoneNumbersEndpoint.getInstance();
